import CTNButton from '@ctn/ui/components/ctn-button';
import CTNButtonsGroup from '@ctn/ui/components/ctn-buttons-group';
import CTNInput from '@ctn/ui/components/ctn-input';
import useModal from '@ctn/ui/components/ctn-modal/hooks/useModal';
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import { useEffect, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import FieldDisplay from '@/components/FieldDisplay';
import PageHeader from '@/components/PageHeader';
import { useGlobalContext } from '@/context/global.context';
import { usePageContext } from '@/context/page.context';
import useApiData from '@/hooks/useApiData';
import useRouteTramite from '@/hooks/useRouteTramite';
import { getDetailSubsanaciones } from '@/services/tramite.service';

import { ESTADO_SUBSANACION, SUBSANACION_ESTADO_COLOR, TIPO_SUBSANACION } from '@/constants/tramite.constants';
import { getBreadcrumbTramite } from '@/helpers/navigation';
import { notificationsConfig } from '@/helpers/notifications';
import { type ISubsanacionDocumento, type ISubsanacionSujeto } from '@/interfaces/model/don.interface';

import DocumentoSection from './DocumentoSection';
import SujetoSection from './SujetoSection';
import { ROUTES_LABEL } from '../../constants/routes.constants';

const TramiteSubsanacionesDetalle = (): ReactElement => {
	const { idTramite, idTipoSubsanacion, idSubsanacion } = useParams();
	const subsanacionModal = useModal({});

	const { user, getTramiteData } = useGlobalContext();
	const ROUTES_TRAMITE = useRouteTramite();
	const { appendBreadcrumb, onBackClick } = usePageContext();
	const { openNotification, closeNotification } = useNotification();
	const notificationTemplate = notificationsConfig({ closeNotification });
	const { triggerFetch } = useApiData({ loading: ['getData' as const] });

	const { control, reset, watch } = useForm<ISubsanacionDocumento | ISubsanacionSujeto>();

	const estado = watch('estado');
	const tipo = watch('tipo');

	const isSujeto = TIPO_SUBSANACION.SUJETO === tipo?.id;
	const isDocumento = TIPO_SUBSANACION.DOCUMENTO === tipo?.id;
	const isSubsanable = ESTADO_SUBSANACION.PENDIENTE === estado?.id;

	const getData = async () => {
		if (!user || !idTramite || !idTipoSubsanacion || !idSubsanacion) {
			openNotification(
				notificationTemplate.errorMessage('No se pudo cargar la información necesaria para el detalle.'),
			);
			return;
		}

		const { data } = await triggerFetch({
			payload: {
				nifEmisor: user.nifEmisor,
				idTipoSector: user.tipoSectorEmisor.id,
				idTramite: Number(idTramite),
				idTipoSubsanacion: Number(idTipoSubsanacion),
				idSubsanacion: Number(idSubsanacion),
			},
			fetch: getDetailSubsanaciones,
			name: 'getData',
			error: 'Error al cargar el detalle',
		});

		if (data) {
			reset(data);
		}
	};

	const onSubmitSubsanacion = () => {
		getData();
	};

	useEffect(() => {
		appendBreadcrumb(ROUTES_LABEL.SUBSANACIONES_DETALLE, [
			getBreadcrumbTramite(getTramiteData()),
			{ label: ROUTES_LABEL.SUBSANACIONES, url: ROUTES_TRAMITE.SUBSANACIONES },
			{ label: ROUTES_LABEL.SUBSANACIONES_DETALLE, url: location.pathname },
		]);
		getData();
	}, []);

	return (
		<>
			<PageHeader hasHelp title={ROUTES_LABEL.SUBSANACIONES_DETALLE} className="u-mt-8" />
			<div className="[ c-pnp-box c-pnp-box--border c-pnp-box--breakout ] [ u-mt-24 u-flow-border ]">
				<div className="c-pnp-box__body">
					<div className="[ o-grid ] [ c-pnp-form-group c-pnp-form-group--accent c-pnp-form-group--radius c-pnp-form-group--display-only ] [ u-flow u-mt-16 ]">
						<div className="o-grid__row">
							<CTNInput
								noFeedBack
								displayOnly
								condensed
								type="text"
								classNameField="o-grid__col-12 o-grid__col-4@portrait"
								name="fechaCreacion"
								label="Fecha alta"
								control={control}
							/>
							<CTNInput
								noFeedBack
								displayOnly
								condensed
								type="text"
								classNameField="o-grid__col-12 o-grid__col-4@portrait"
								name="tramite.numeroTramite"
								label="Núm solicitud"
								control={control}
							/>
							<FieldDisplay
								noFeedBack
								condensed
								displayOnly
								className="o-grid__col-12 o-grid__col-4@portrait"
								name="estado.descripcion"
								label="Estado"
								iconLeft={{
									icon: 'status',
									color:
										SUBSANACION_ESTADO_COLOR[estado?.id as keyof typeof SUBSANACION_ESTADO_COLOR] ??
										'gray-50',
								}}
								control={control}
							/>
						</div>
						<div className="o-grid__row">
							<CTNInput
								noFeedBack
								displayOnly
								condensed
								type="text"
								classNameField="o-grid__col-12 o-grid__col-4@portrait"
								name="observacionesPeticion"
								label="Motivo de la subsanación"
								control={control}
								maxLength={32}
							/>
						</div>
						{!isSubsanable && (
							<>
								<div className="o-grid__row">
									<CTNInput
										noFeedBack
										displayOnly
										condensed
										type="text"
										classNameField="o-grid__col-12 o-grid__col-4@portrait"
										label="Fecha subsanación"
										name="fechaSubsanacion"
										control={control}
									/>
								</div>
								<div className="o-grid__row">
									<CTNInput
										noFeedBack
										displayOnly
										condensed
										type="text"
										classNameField="o-grid__col-12"
										name="observacionesSubsanacion"
										label="Comentarios de la empresa"
										control={control}
										maxLength={32}
									/>
								</div>
							</>
						)}
					</div>
					{isDocumento && (
						<DocumentoSection
							className="u-mt-32"
							data={watch() as ISubsanacionDocumento}
							isSubsanable={isSubsanable}
							isShowSubsanacion={subsanacionModal.isShowing}
							onSubmitSubsanacion={onSubmitSubsanacion}
							onCloseSubsanacion={subsanacionModal.closeModal}
						/>
					)}
					{isSujeto && (
						<SujetoSection
							className="u-mt-32"
							data={watch() as ISubsanacionSujeto}
							isShowSubsanacion={subsanacionModal.isShowing}
							onSubmitSubsanacion={onSubmitSubsanacion}
							onCloseSubsanacion={subsanacionModal.closeModal}
						/>
					)}
				</div>
				<footer className="c-pnp-box__footer">
					<CTNButtonsGroup align="right">
						<CTNButton
							label={isSubsanable ? 'Cancelar' : 'Cerrar'}
							mode="tertiary"
							minWidth="140"
							size="md"
							onClick={() => onBackClick()}
						/>
						{isSubsanable && (
							<CTNButton
								label="Subsanar"
								mode="primary"
								minWidth="140"
								size="md"
								onClick={subsanacionModal.openModal}
							/>
						)}
					</CTNButtonsGroup>
				</footer>
			</div>
		</>
	);
};

export default TramiteSubsanacionesDetalle;
